import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { loadSpeechComponents, topicLessonsDataUrl, dataImagesUrl } from '../../services/dataApi';
import { loadTopicLesson } from '../../services/topicLessons';
import { TopicProgressService } from '../../services/TopicProgressService';
import { QuestionCard } from '../exercise/ExercisePage';
import { ChunkyButton, Confetti, FeedbackDrawer, GameProgressBar } from '../../components/GameUI';

const PLAYER_COLOR = '#7C3AED';

const LEARNING_TIP_ICONS = { tip: '⭐' };

function speak(text, lang) {
  if (!text || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  if (lang) utterance.lang = lang;
  window.speechSynthesis.speak(utterance);
}

function textValue(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean).join(' ');
  if (value && typeof value === 'object' && 'txt' in value) return textValue(value.txt);
  return value == null ? '' : String(value);
}

export function orderedLessonLines(section) {
  if (!section || typeof section !== 'object') return [];
  return Object.entries(section)
    .filter(([key, line]) => /^line\d+$/.test(key) && line)
    .sort(([first], [second]) => Number(first.replace('line', '')) - Number(second.replace('line', '')))
    .map(([, line]) => textValue(line))
    .filter(Boolean);
}

export function topicQuestion(item, imageUrl) {
  const sentenceText = textValue(item.sentence);
  return {
    ...item,
    category: item.type,
    question: textValue(item.question),
    sentence: item.sentence?.hidden ? null : sentenceText,
    expectedText: item.type === 'speaking' ? sentenceText || textValue(item.answer) : item.expectedText,
    audioText: item.type === 'listen' ? sentenceText : item.audioText,
    audio: null,
    image: imageUrl || null,
    // options stay as {txt, translate, translationHint} objects — QuestionCard
    // reads value.txt directly (matches the exam pack shape it also renders).
    options: item.options || {},
    // QuestionCard's match UI reads left[].text/right[].text (matches the exam
    // pack shape); lesson data stores left[].txt/right[].txt, so translate here.
    left: (item.left || []).map((entry) => ({ ...entry, text: textValue(entry) })),
    right: (item.right || []).map((entry) => ({ ...entry, text: textValue(entry) })),
    explanation: textValue(item.explanation),
    listenQuiz: item.listenQuiz || item.type === 'listen',
    speakPractice: item.speakPractice || item.type === 'speaking',
  };
}

function lessonAssetUrl(language, folder, filename) {
  if (!filename || !/^[a-z0-9_.-]+$/i.test(filename)) return null;
  return topicLessonsDataUrl(`${language}/${folder}/${filename}`);
}

const SPEECH_IMAGE_LABELS = {
  tonguePosition: 'speechImageTonguePosition',
  tongueHeight: 'speechImageTongueHeight',
  lips: 'speechImageLips',
  vocalFolds: 'speechImageVocalFolds',
  airflow: 'speechImageAirflow',
  jaw: 'speechImageJaw',
};

const SECTION_ICONS = { mouthPosition: '👄', soundProduction: '🔊' };
const COMPONENT_ICONS = {
  tonguePosition: '👅',
  tongueHeight: '👅',
  lips: '👄',
  jaw: '😮',
  vocalFolds: '🎤',
  airflow: '💨',
};

// Renders item.pronunciation.components (id -> stateId refs, e.g.
// { tonguePosition: 'central' }) against the shared speech_components.json
// definitions, grouped by each component's `section` (mouthPosition /
// soundProduction) — mirrors the two-panel layout used across the German
// alphabet lessons.
function PronunciationDiagram({ pronunciation, speechComponents, t }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const sections = useMemo(() => {
    const grouped = { mouthPosition: [], soundProduction: [] };
    Object.entries(pronunciation.components || {}).forEach(([componentId, stateId]) => {
      const componentDef = speechComponents.components?.[componentId];
      const stateDef = componentDef?.states?.[stateId];
      if (!componentDef || !stateDef) return;
      (grouped[componentDef.section] || (grouped[componentDef.section] = [])).push({
        componentId,
        label: componentDef.label?.txt || componentId,
        ...stateDef,
      });
    });
    return grouped;
  }, [pronunciation, speechComponents]);

  return (
    <div className="mt-6 space-y-5">
      {[
        { key: 'mouthPosition', title: t('journeyPages.mouthPositionTitle'), desc: t('journeyPages.mouthPositionDesc') },
        { key: 'soundProduction', title: t('journeyPages.soundProductionTitle'), desc: t('journeyPages.soundProductionDesc') },
      ].map(({ key, title, desc }) => {
        const cards = sections[key];
        if (!cards || cards.length === 0) return null;
        return (
          <div key={key}>
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-violet-600 text-base text-white" aria-hidden="true">{SECTION_ICONS[key]}</span>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-violet-700">{title}</p>
                <p className="text-[11px] font-semibold text-slate-400">{desc}</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {cards.map((card) => {
                const infoTxt = textValue(card.info);
                return (
                  <div key={card.componentId} className="rounded-2xl border border-violet-100 bg-white p-3 text-center shadow-sm">
                    <img src={card.image} alt={card.label} className="mx-auto h-16 w-full object-contain" />
                    <p className="mt-2 text-[11px] font-black uppercase tracking-wide text-slate-500">{COMPONENT_ICONS[card.componentId]} {card.label}</p>
                    <p className="text-sm font-bold text-violet-700">{card.txt}</p>
                    {infoTxt && (
                      <>
                        <button
                          type="button"
                          onClick={() => toggle(card.componentId)}
                          aria-label={t('journeyPages.moreInfo')}
                          aria-expanded={expanded.has(card.componentId)}
                          className="mx-auto mt-2 grid h-6 w-6 place-items-center rounded-full border border-slate-200 text-[10px] font-black text-slate-400 transition hover:bg-slate-50"
                        >
                          i
                        </button>
                        {expanded.has(card.componentId) && (
                          <p className="mt-2 text-left text-[11px] leading-snug text-slate-500">{infoTxt}</p>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ReadingMaterial({ item, imageUrl, audioUrl, speechComponents, ttsCode, t }) {
  const headings = orderedLessonLines(item.h1);
  const translations = orderedLessonLines(item.h2);
  const tipTxt = textValue(item.learningTip);
  const tipIcon = LEARNING_TIP_ICONS[item.learningTip?.icon] || LEARNING_TIP_ICONS.tip;
  const practiceLabel = textValue(item.practice?.label) || (headings.length ? `${t('journeyPages.practice')} ${headings.join(' ')}` : '');
  const notes = Array.isArray(item.p1?.txt) ? item.p1.txt : item.p1?.txt ? [item.p1.txt] : [];
  const speechImages = Object.entries(item.speechImages || {}).filter(([, src]) => src);
  // Alphabet-style single-glyph lessons (item.pronunciation present) get the
  // big letter + speaker + IPA header laid out beside the note box, instead
  // of the generic stacked headings/translations/note used by every other
  // reading-material lesson type (grammar, vocab intros, ...).
  const isLetterCard = Boolean(item.pronunciation);
  const categoryTxt = speechComponents?.categories?.[item.pronunciation?.category]?.txt;
  const notesBlock = notes.length > 0 && (
    <aside className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-950">
      <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-amber-700">💡 {t('journeyPages.note')}</p>
      <div className="space-y-2">{notes.map((note, index) => <p key={`${note}-${index}`}>{note}</p>)}</div>
    </aside>
  );

  return (
    <article className="rounded-[26px] border border-violet-100 bg-white p-5 shadow-sm sm:p-7">
      {!isLetterCard && (
        <span className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-violet-700">
          {item.type === 'recap' ? t('journeyPages.reviewMaterial') : t('journeyPages.readingMaterial')}
        </span>
      )}
      {imageUrl && <img src={imageUrl} alt="" className="mx-auto mt-5 max-h-64 rounded-2xl object-contain" />}
      {isLetterCard ? (
        <div className={`flex flex-col gap-4 sm:flex-row sm:items-center ${notesBlock ? 'sm:items-start' : ''}`}>
          <div className="flex shrink-0 items-center gap-3" dir="auto">
            <div>
              {headings.map((line, index) => (
                <p key={`${line}-${index}`} className={`font-black leading-none text-violet-700 ${line.length > 2 ? 'text-3xl' : 'text-6xl'}`}>{line}</p>
              ))}
              {translations.length > 0 && <p className="mt-1 text-lg font-bold text-slate-400">{translations.join(' ')}</p>}
              {categoryTxt && <p className="mt-1 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-violet-600">{categoryTxt}</p>}
            </div>
            {headings.length > 0 && (
              <button
                type="button"
                onClick={() => speak(headings.join(' '), ttsCode)}
                aria-label={practiceLabel || t('journeyPages.practice')}
                className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-violet-100 text-violet-700 transition hover:bg-violet-200"
              >
                <span aria-hidden="true" className="text-xl">🔊</span>
              </button>
            )}
          </div>
          {notesBlock && <div className="flex-1">{notesBlock}</div>}
        </div>
      ) : (
        <>
          {headings.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-3" dir="auto">
              {headings.map((line, index) => (
                <h2 key={`${line}-${index}`} className="rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 px-5 py-3 text-2xl font-black text-white shadow-md sm:text-3xl">{line}</h2>
              ))}
            </div>
          )}
          {translations.length > 0 && (
            <div className="mt-4 flex flex-col items-start gap-2 border-l-4 border-violet-200 pl-4" dir="auto">
              {translations.map((line, index) => <p key={`${line}-${index}`} className="text-lg font-bold text-slate-700">{line}</p>)}
            </div>
          )}
          {notesBlock && <div className="mt-6">{notesBlock}</div>}
        </>
      )}
      {speechImages.length > 0 && (
        <div className="mt-6">
          <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-violet-500">{t('journeyPages.speechImagesHeading')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {speechImages.map(([key, src]) => (
              <div key={key} className="rounded-2xl border border-violet-100 bg-violet-50/50 p-3 text-center">
                <img src={src} alt={t(`journeyPages.${SPEECH_IMAGE_LABELS[key] || key}`)} className="mx-auto h-20 w-full object-contain" />
                <p className="mt-2 text-xs font-bold text-slate-600">{t(`journeyPages.${SPEECH_IMAGE_LABELS[key] || key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {item.pronunciation && speechComponents && (
        <PronunciationDiagram pronunciation={item.pronunciation} speechComponents={speechComponents} t={t} />
      )}
      {tipTxt && (
        <div className="mt-5 flex items-start gap-2 rounded-2xl bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-900">
          <span aria-hidden="true">{tipIcon}</span>
          <p>{tipTxt}</p>
        </div>
      )}
      {item.practice?.enabled && practiceLabel && (
        <button
          type="button"
          onClick={() => speak(headings.join(' ') || practiceLabel, ttsCode)}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 py-3 text-sm font-black text-white shadow-md transition hover:bg-violet-700"
        >
          <span aria-hidden="true">🔊</span>
          {practiceLabel}
        </button>
      )}
      {audioUrl && <audio controls className="mt-5 w-full" src={audioUrl}>{t('journeyPages.audioUnsupported')}</audio>}
    </article>
  );
}

export function ConversationMaterial({ item, t }) {
  const firstSpeaker = item.lines?.[0]?.speaker;
  return (
    <article className="rounded-[26px] border border-sky-100 bg-white p-5 shadow-sm sm:p-7">
      <span className="inline-flex rounded-full bg-sky-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-sky-700">{t('journeyPages.conversationMaterial')}</span>
      <div className="mt-5 space-y-4">
        {(item.lines || []).map((line, index) => {
          const onLeft = line.speaker === firstSpeaker;
          return (
            <div key={`${line.speaker}-${index}`} className={`flex ${onLeft ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[85%] ${onLeft ? 'text-left' : 'text-right'}`}>
                <p className="mb-1 px-2 text-[10px] font-black uppercase tracking-wider text-slate-400">{line.speaker}</p>
                <div className={`rounded-2xl px-4 py-3 text-sm font-bold shadow-sm sm:text-base ${onLeft ? 'rounded-bl-md bg-sky-100 text-sky-950' : 'rounded-br-md bg-violet-600 text-white'}`} dir="auto">
                  {textValue(line.txt)}
                </div>
                {(line.translation || line.translationHint) && <p className="mt-1 px-2 text-xs text-slate-500">{line.translation || line.translationHint}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

// Renders a `type: "table"` card — a reference table (columns/rows of plain
// strings) embedded in an otherwise card-by-card lesson, e.g. letter-tables
// in common-foundations/pronunciation-guide/alphabet lessons. Unlike the
// other card types there's no h1/h2/p1, just a title plus a small table.
export function TableMaterial({ item }) {
  const title = textValue(item.title);
  const columns = (item.columns || []).map(textValue);
  const rows = (item.rows || []).map((row) => row.map(textValue));
  return (
    <article className="rounded-[26px] border border-violet-100 bg-white p-5 shadow-sm sm:p-7">
      {title && <h2 className="mb-4 text-lg font-black text-slate-900">{title}</h2>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-indigo-50">
              {columns.map((col) => (
                <th key={col} className="whitespace-nowrap px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-indigo-700">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={`border-b border-slate-100 ${ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 align-middle text-slate-700">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

// Renders one block of a `type: "section"` card's content[] array. Each
// block keeps its own type so a section can mix headings/paragraphs/lists
// freely — table/conversation blocks reuse the exact same shape their
// top-level item types already have, so TableMaterial/ConversationMaterial
// render them directly without a wrapping <article> card of their own.
function SectionBlock({ block, t }) {
  switch (block?.type) {
    case 'heading':
      return <h3 className="mt-6 text-lg font-black text-slate-900 first:mt-0" dir="auto">{textValue(block.txt)}</h3>;
    case 'para':
      return <p className="mt-3 text-sm leading-relaxed text-slate-700" dir="auto">{textValue(block.txt)}</p>;
    case 'points':
      return (
        <ul className="mt-3 space-y-2">
          {(block.items || []).map((point, index) => (
            <li key={index} className="flex gap-2 text-sm leading-relaxed text-slate-700" dir="auto">
              <span className="mt-1 text-violet-500" aria-hidden="true">•</span>
              <span>{textValue(point)}</span>
            </li>
          ))}
        </ul>
      );
    case 'table':
      return <TableMaterial item={block} />;
    case 'conversation':
      return <ConversationMaterial item={block} t={t} />;
    default:
      return null;
  }
}

// Renders a `type: "section"` card — an ordered content[] array of
// heading/para/points/table/conversation blocks, for long-form content
// (an overview article, a grammar explanation) that doesn't fit intro's
// fixed h1/h2/p1 slots. Works the same whether the lesson is interactive
// (one section per swipeable card) or static (several sections stacked).
export function SectionMaterial({ item, t }) {
  const content = Array.isArray(item.content) ? item.content : [];
  return (
    <article className="rounded-[26px] border border-violet-100 bg-white p-5 shadow-sm sm:p-7">
      {content.map((block, index) => <SectionBlock key={index} block={block} t={t} />)}
    </article>
  );
}

export default function TopicLessonPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { level: rawLevel, lessonId } = useParams();
  const { languagePrefs } = useAuth();
  const { langMap } = useLangMap();
  const level = String(rawLevel || 'a1').toLowerCase();
  const targetCode = languagePrefs?.targetLanguage || '';
  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const langPair = languagePrefs ? `${nativeCode}_${targetCode}` : 'guest';
  const [lesson, setLesson] = useState(null);
  const [status, setStatus] = useState('loading');
  const [index, setIndex] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [finished, setFinished] = useState(false);
  const [speechComponents, setSpeechComponents] = useState(null);

  useEffect(() => {
    let active = true;
    loadSpeechComponents(nativeCode).then((data) => { if (active) setSpeechComponents(data); }).catch(() => {});
    return () => { active = false; };
  }, [nativeCode]);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setLesson(null);
    setIndex(0);
    setFeedback(null);
    setFinished(false);
    loadTopicLesson(targetCode, level, lessonId, { nativeCode })
      .then((loaded) => {
        if (!active) return;
        setLesson(loaded);
        setStatus(loaded.items.length ? 'ready' : 'unavailable');
      })
      .catch(() => { if (active) setStatus('unavailable'); });
    return () => { active = false; };
  }, [lessonId, level, targetCode, nativeCode]);

  const items = lesson?.items || [];
  const item = items[index];
  const targetDir = langMap[targetCode]?.dir || 'ltr';
  const nativeDir = langMap[nativeCode]?.dir || 'ltr';
  const ttsCode = langMap[targetCode]?.tts || targetCode;
  const chapterIds = useMemo(() => (lesson?.chapters || []).map((chapter) => chapter.id), [lesson]);

  const completeLesson = () => {
    if (!lesson) return;
    TopicProgressService.completeLesson(langPair, targetCode, level, lesson.chapter.id, chapterIds);
    setFinished(true);
  };

  const continueLesson = () => {
    setFeedback(null);
    if (index + 1 >= items.length) completeLesson();
    else setIndex((current) => current + 1);
  };

  if (status === 'loading') {
    return <div className="page-shell"><div className="h-72 animate-pulse rounded-3xl bg-violet-100" aria-label={t('journeyPages.lessonLoading')} /></div>;
  }

  if (status === 'unavailable' || !lesson) {
    return (
      <div className="page-shell text-center">
        <div className="rounded-3xl border border-rose-200 bg-white p-10 shadow-sm">
          <span className="text-5xl" aria-hidden="true">📕</span>
          <h1 className="mt-4 text-2xl font-black text-slate-900">{t('journeyPages.lessonUnavailable')}</h1>
          <Link to={`/topic-lessons/${level}`} className="mt-5 inline-flex rounded-xl bg-violet-600 px-5 py-3 text-sm font-black text-white">{t('journeyPages.backToTopics')}</Link>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="relative grid min-h-[calc(100vh-6rem)] place-items-center bg-gradient-to-br from-violet-50 to-emerald-50 px-4 text-center">
        <Confetti />
        <section className="relative w-full max-w-lg rounded-3xl border border-emerald-200 bg-white p-8 shadow-xl sm:p-10">
          <span className="text-7xl" aria-hidden="true">🏆</span>
          <h1 className="mt-5 text-3xl font-black text-slate-900">{t('journeyPages.lessonFinished')}</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{t('journeyPages.lessonFinishedDesc')}</p>
          <ChunkyButton color="#10B981" onClick={() => navigate(`/topic-lessons/${level}`)} className="mt-7 w-full py-4 text-white">{t('journeyPages.backToTopics')}</ChunkyButton>
        </section>
      </div>
    );
  }

  const imageName = Array.isArray(item.image) ? item.image[0] : item.image;
  const imageUrl = imageName && imageName.startsWith('/data-images/')
    ? dataImagesUrl(imageName)
    : lessonAssetUrl(targetCode, 'images', imageName);
  const audioUrl = lessonAssetUrl(targetCode, 'sounds', item.audio);
  const isReading = item.type === 'intro' || item.type === 'recap';
  const isConversation = item.type === 'conversation';
  const isSection = item.type === 'section';
  const question = !isReading && !isConversation && !isSection ? topicQuestion(item, imageUrl) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      <header className="border-b border-violet-100 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <button type="button" onClick={() => navigate(`/topic-lessons/${level}`)} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl text-slate-400 transition hover:bg-slate-100" aria-label={t('journeyPages.backToTopics')}>✕</button>
          <div className="min-w-0 flex-1">
            <GameProgressBar value={items.length ? index / items.length : 0} color={PLAYER_COLOR} />
            <p className="mt-1 truncate text-[10px] font-black uppercase tracking-wider text-violet-500">{lesson.chapter.label}</p>
          </div>
          <span className="shrink-0 rounded-full bg-violet-100 px-3 py-1.5 text-xs font-black text-violet-700">{index + 1}/{items.length}</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-7" style={{ paddingBottom: feedback ? 180 : 28 }}>
        {isReading && <ReadingMaterial item={item} imageUrl={imageUrl} audioUrl={audioUrl} speechComponents={speechComponents} ttsCode={ttsCode} t={t} />}
        {isConversation && <ConversationMaterial item={item} t={t} />}
        {isSection && <SectionMaterial item={item} t={t} />}
        {question && (
          <QuestionCard
            key={`${lesson.chapter.id}-${index}`}
            question={question}
            langCode={targetCode}
            ttsCode={ttsCode}
            targetDir={targetDir}
            nativeDir={nativeDir}
            color={PLAYER_COLOR}
            onMarked={(correct, correctAnswer, explanation) => setFeedback({ correct, correctAnswer, explanation })}
            t={t}
          />
        )}

        {(isReading || isConversation || isSection) && (
          <ChunkyButton color={PLAYER_COLOR} onClick={continueLesson} className="mt-5 w-full py-4 text-white">
            {index + 1 >= items.length ? t('journeyPages.finishLesson') : t('game.continue')}
          </ChunkyButton>
        )}
      </main>

      {feedback && (
        <FeedbackDrawer
          correct={feedback.correct}
          title={feedback.correct ? t('game.praises', { returnObjects: true })[0] : t('game.incorrectTitle')}
          subtitle={feedback.correct ? feedback.explanation : feedback.correctAnswer ? t('game.correctAnswerLabel', { answer: feedback.correctAnswer }) : feedback.explanation}
          continueLabel={index + 1 >= items.length ? t('journeyPages.finishLesson') : t('game.continue')}
          onContinue={continueLesson}
        />
      )}
    </div>
  );
}
